export type EditorCorrelationContext = {
  jobId?: string | null;
  restoreRequestId?: string | null;
  viewrGCSPath?: string | null;
  saveAttemptId?: string | null;
  unityCommand?: string | null;
  phase?: string | null;
  source?: string | null;
};

type EditorCorrelationLogLevel = "log" | "warn" | "error";

const sanitizeEditorCorrelationContext = (
  context: EditorCorrelationContext | null | undefined,
): Record<string, string> => {
  if (!context) return {};

  return Object.fromEntries(
    Object.entries(context).filter(
      (entry): entry is [string, string] =>
        typeof entry[1] === "string" && entry[1].trim().length > 0,
    ),
  );
};

export const mergeEditorCorrelationContext = (
  ...contexts: Array<EditorCorrelationContext | null | undefined>
): EditorCorrelationContext => {
  return contexts.reduce<EditorCorrelationContext>(
    (acc, context) => ({
      ...acc,
      ...context,
    }),
    {},
  );
};

export const logEditorCorrelation = (
  event: string,
  context?: EditorCorrelationContext | null,
  details?: Record<string, unknown> | null,
  level: EditorCorrelationLogLevel = "log",
): void => {
  const logger = console[level] ?? console.log;
  const correlation = sanitizeEditorCorrelationContext(context);

  logger(`[EditorFlow] ${event}`, {
    ...(Object.keys(correlation).length > 0 ? { correlation } : {}),
    ...(details ?? {}),
  });
};

/**
 * `reactToUnity_loadViewR` 등 로드 페이로드에 넣는 트랜잭션 ID.
 * Unity `BuildSpaceNavigationStatePayload`는 transactionId가 비어 있으면 null을 반환해
 * `SPACE_NAVIGATION_STATE`(및 VSE lifecycle의 `vse_model_ready`)가 React에 도달하지 않을 수 있다.
 */
export function createViewrLoadTransactionId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `viewr_load_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}
